import FieldRenderer from './FieldRenderer';
import { formatDate } from '../../utils/formatUtils';

function displayDecimal(val) {
  if (val == null || val === '') return null;
  if (typeof val === 'number') return String(val);
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val !== null && typeof val.toString === 'function') {
    const s = val.toString();
    return s === '[object Object]' ? null : s;
  }
  return String(val);
}

/**
 * Renders every enrollment for a student (batch, course, status, placement, fees, etc.).
 * Used on filter list expand and student management detail row.
 */
const StudentEnrollmentsSections = ({
  enrollments = [],
  domain = null,
  /** When true, show student-level domain in each enrollment card (filter page). */
  repeatDomain = false,
  /** Shown only when the student has more than one enrollment. */
  onRemoveEnrollment,
  removingEnrollmentId = null,
}) => {
  const list = Array.isArray(enrollments) ? enrollments : [];

  const renderField = (label, value, type = 'text') => (
    <FieldRenderer label={label} value={value} type={type} />
  );

  if (list.length === 0) {
    return (
      <div className="space-y-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
        <h4 className="text-sm font-bold uppercase tracking-wide text-gray-800">📚 Enrollments</h4>
        <p className="text-sm text-gray-500">No enrollments on record.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-bold uppercase tracking-wide border-b border-gray-200 pb-2 text-gray-800">
        📚 Enrollments ({list.length})
      </h4>
      {list.map((en, idx) => {
        const batch = en.batch;
        const course = batch?.course;
        const category = course?.category;
        const placement = en.placement;
        const fee = en.feeDetails;
        const readiness = en.interviewReadiness;
        const sessions = en.counsellingSessions || [];
        const mocks = en.mockInterviews || [];
        const opportunities = en.placementOpportunities || [];
        const concerns = en.trainingConcerns || [];
        const progress = en.moduleProgress || [];
        const testimony = en.testimony;

        return (
          <div
            key={en.id != null ? String(en.id) : `enrollment-${idx}`}
            className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2">
              <span className="text-sm font-semibold text-gray-900">
                Enrollment #{idx + 1}
                {batch?.batchName ? ` — ${batch.batchName}` : ''}
              </span>
              <span className="flex items-center gap-2">
                {en.batchStatus ? (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-800">
                    {en.batchStatus}
                  </span>
                ) : null}
                {list.length > 1 && typeof onRemoveEnrollment === 'function' ? (
                  <button
                    type="button"
                    className="text-sm font-medium text-red-700 hover:text-red-900 disabled:opacity-50"
                    disabled={String(removingEnrollmentId) === String(en.id)}
                    onClick={() => onRemoveEnrollment(en)}
                  >
                    {String(removingEnrollmentId) === String(en.id) ? 'Removing…' : 'Remove'}
                  </button>
                ) : null}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <div className="space-y-2">
                <h5 className="text-xs font-bold uppercase text-gray-500">Course & batch</h5>
                {repeatDomain && domain
                  ? renderField('Domain', domain.domainName || domain.domain_name)
                  : null}
                {renderField('Category', category?.categoryName)}
                {renderField('Course', course?.courseName)}
                {renderField('Course code', course?.courseCode)}
                {renderField('Batch', batch?.batchName)}
                {renderField('Batch ID', batch?.id)}
                {renderField('Mode', batch?.mode)}
                {renderField('Location', batch?.location)}
                {renderField('Batch status (schedule)', batch?.status)}
                {renderField('Batch start', batch?.startDate, 'date')}
                {renderField('Tentative end', batch?.tentativeEndDate, 'date')}
                {renderField('Batch end', batch?.endDate, 'date')}
              </div>

              <div className="space-y-2">
                <h5 className="text-xs font-bold uppercase text-gray-500">Enrollment record</h5>
                {renderField('Enrollment ID', en.id != null ? String(en.id) : null)}
                {renderField('Enrollment date', en.enrollmentDate, 'date')}
                {renderField('Course completion', en.courseCompletionDate, 'date')}
                {renderField('Attendance %', displayDecimal(en.attendancePercentage))}
                {renderField('Placement status', en.placementStatus)}
                {renderField('In placement group', en.inPlacementGroup, 'boolean')}
                {renderField('Applied for Truechip', en.appliedForTruechip, 'boolean')}
                {renderField('Truechip result', en.truechipResult)}
                {renderField('Remarks', en.remarks)}
                {renderField('Certification issued', en.certificationIssued, 'boolean')}
                {renderField('Video testimony', en.videoTestimonyReceived, 'boolean')}
                {renderField('Testimony (enrollment)', en.testimonyReceived, 'boolean')}
                {renderField('Insurance taken', en.insuranceTaken, 'boolean')}
                {renderField('Insurance deactivated', en.insuranceDeactivated, 'boolean')}
                {renderField('Created', en.createdAt, 'date')}
                {renderField('Updated', en.updatedAt, 'date')}
              </div>

              <div className="space-y-2">
                <h5 className="text-xs font-bold uppercase text-gray-500">Placement</h5>
                {placement ? (
                  <>
                    {renderField('Company', placement.company?.companyName)}
                    {renderField('Job role', placement.jobRole)}
                    {renderField('Placement date', placement.placementDate, 'date')}
                    {renderField('Joining date', placement.joiningDate, 'date')}
                    {renderField('Starting package', displayDecimal(placement.startingPackage))}
                    {renderField('Current package', displayDecimal(placement.currentPackage))}
                    {renderField('Feedback', placement.feedbackRemarks)}
                    {renderField('Part of alumni', placement.partOfAlumniGroup, 'boolean')}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">No placement record for this enrollment.</p>
                )}
              </div>

              {fee ? (
                <div className="space-y-2">
                  <h5 className="text-xs font-bold uppercase text-gray-500">Fee</h5>
                  {renderField('Fee submitted', fee.feeSubmitted, 'boolean')}
                  {renderField('Fee amount', displayDecimal(fee.feeAmount))}
                  {renderField('Payment method', fee.paymentMethod)}
                  {renderField('Payment date', fee.paymentDate, 'date')}
                  {renderField('Deadline', fee.paymentDeadline, 'date')}
                  {renderField('Transaction ref', fee.transactionReference)}
                  {renderField('Payment issue', fee.paymentIssue)}
                </div>
              ) : null}

              {readiness ? (
                <div className="space-y-2">
                  <h5 className="text-xs font-bold uppercase text-gray-500">Interview readiness</h5>
                  {renderField('In placement group', readiness.inPlacementGroup, 'boolean')}
                  {renderField('In mock group', readiness.inMockGroup, 'boolean')}
                  {renderField('In underprepared group', readiness.inUnderpreparedGroup, 'boolean')}
                  {renderField('Interview ready', readiness.interviewReady, 'boolean')}
                  {renderField('All modules complete', readiness.allModulesComplete, 'boolean')}
                  {renderField('Project submitted', readiness.projectSubmitted, 'boolean')}
                  {renderField('Student readiness', readiness.studentReadiness, 'boolean')}
                  {renderField('Trainer readiness', readiness.trainerReadiness, 'boolean')}
                  {renderField('Resume submitted', readiness.resumeReadySubmitted, 'boolean')}
                  {renderField('Resume deadline', readiness.resumeSubmissionDeadline, 'date')}
                  {renderField('Resume reviewed', readiness.resumeReviewed, 'boolean')}
                  {renderField('Resume changes needed', readiness.resumeChangesNeeded, 'boolean')}
                  {renderField('Mock interview done', readiness.mockInterviewDone, 'boolean')}
                  {renderField('# Mock interviews', readiness.numberOfMockInterviews)}
                  {renderField('Communication rating', readiness.communicationSkillsRating)}
                  {renderField('Attitude rating', readiness.attitudeRating)}
                  {renderField('Presentation rating', readiness.presentationRating)}
                  {renderField('Technical rating', readiness.technicalReadinessRating)}
                  {renderField('Areas to improve', readiness.areasToImprove)}
                  {renderField('Feedback score', readiness.feedbackScore)}
                </div>
              ) : null}

              {testimony ? (
                <div className="space-y-2">
                  <h5 className="text-xs font-bold uppercase text-gray-500">Testimony</h5>
                  {renderField('Video testimony received', testimony.videoTestimonyReceived, 'boolean')}
                  {renderField('Text testimony received', testimony.textTestimonyReceived, 'boolean')}
                  {renderField('Text content', testimony.textTestimonyContent)}
                  {renderField('Wants to contribute as trainer', testimony.wantToContributeAsTrainer, 'boolean')}
                  {renderField('Feedback to improve training', testimony.feedbackToImproveTraining)}
                  {renderField('Updated', testimony.updatedAt, 'date')}
                </div>
              ) : null}
            </div>

            {sessions.length > 0 ? (
              <div className="mt-4 border-t border-gray-100 pt-3">
                <h5 className="mb-2 text-xs font-bold uppercase text-gray-500">Counselling sessions</h5>
                <div className="grid gap-2 sm:grid-cols-2">
                  {sessions.map((session, sidx) => (
                    <div key={session.id != null ? String(session.id) : sidx} className="rounded border border-blue-100 bg-blue-50/50 p-2 text-sm">
                      {renderField('Date', session.sessionDate, 'date')}
                      {renderField('Batch', session.batch?.batchName || en.batch?.batchName)}
                      {renderField('Counselor', session.counselor?.counselorName)}
                      {renderField('Remarks', session.remarks)}
                      {renderField('Follow-up', session.followUpNeeded, 'boolean')}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {mocks.length > 0 ? (
              <div className="mt-4 border-t border-gray-100 pt-3">
                <h5 className="mb-2 text-xs font-bold uppercase text-gray-500">Mock interviews</h5>
                <ul className="list-inside list-disc space-y-1 text-sm text-gray-800">
                  {mocks.map((m, midx) => (
                    <li key={m.id != null ? String(m.id) : midx}>
                      #{m.interviewNumber}: {m.interviewCategory || '—'} —{' '}
                      {m.interviewDate ? formatDate(m.interviewDate) : '—'}
                      {m.interviewerName ? ` (${m.interviewerName})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {opportunities.length > 0 ? (
              <div className="mt-4 border-t border-gray-100 pt-3">
                <h5 className="mb-2 text-xs font-bold uppercase text-gray-500">Placement opportunities</h5>
                <ul className="space-y-2 text-sm">
                  {opportunities.map((o, oidx) => (
                    <li key={o.id != null ? String(o.id) : oidx} className="rounded border border-amber-100 bg-amber-50/40 p-2">
                      <span className="font-medium">{o.company?.companyName || 'Company'}</span>
                      {o.finalStatus ? ` — ${o.finalStatus}` : ''}
                      {o.interviewDate ? ` — Interview ${formatDate(o.interviewDate)}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {concerns.length > 0 ? (
              <div className="mt-4 border-t border-gray-100 pt-3">
                <h5 className="mb-2 text-xs font-bold uppercase text-gray-500">Training concerns</h5>
                <ul className="space-y-2 text-sm">
                  {concerns.map((c, cidx) => (
                    <li key={c.id != null ? String(c.id) : cidx} className="rounded border border-red-100 bg-red-50/30 p-2">
                      <span className="font-medium">{c.concernType}</span>
                      {c.resolutionStatus ? ` (${c.resolutionStatus})` : ''}: {c.description}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {progress.length > 0 ? (
              <div className="mt-4 border-t border-gray-100 pt-3">
                <h5 className="mb-2 text-xs font-bold uppercase text-gray-500">Module progress</h5>
                <ul className="max-h-40 list-inside list-disc overflow-y-auto text-sm text-gray-800">
                  {progress.map((p, pidx) => (
                    <li key={p.id != null ? String(p.id) : pidx}>
                      {p.module?.moduleName || `Module ${p.moduleId}`}:{' '}
                      {p.isCompleted ? 'Done' : 'Not done'}
                      {p.completionDate ? ` (${formatDate(p.completionDate)})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default StudentEnrollmentsSections;
